import React, { useMemo, useState } from 'react';
import { fetchEmailConfig } from '../services/api';
import { Field, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Configure Site > Email Config: the From, To and BCC addresses of each notification (Azure exit WS8).
 * Reads sql01.EmailConfig. Saving is off until ws8_email_config_live. Not the recipe email settings.
 */

// core/constants/dropdown-options/email-config in the admin portal. A type not here shows as itself.
const LABELS = {
  'Contact': 'Contact Notification',
  'ReShip': 'Reship Notification',
  'ChangeIPAddressEmail': 'Change IP Address Notification',
  'InvalidUserEmail': 'Invalid User Notification',
  'VIPClub': 'VIP Club Notification',
  'OrderFeedBackRequest': 'Order Feedback Request Notification',
  'OrderFeedBackConfirm': 'Order Feedback Confirmation Notification',
  'SendFollowupEmail': 'Follow-Up Notification',
  'CustomerForgotPasswordEmail': 'Forgot Password Notification',
  'DeliveryDateWarningEmail': 'Delivery Date Warning Notification',
  'Yield Sheet Report': 'Yield Sheet Report Notification',
  'OverWeightEmail': 'Overweight Order Notification',
  'HawaiiDeliverWarning': 'Hawaii Delivery Warning Notification',
  'LowYieldWarning': 'Low Yield Warning Notification',
  'InventoryLimitAlert': 'Inventory Limit Alert Notification',
  'InventoryLimitAlert': 'Inventory Limit Alert Notification',
  'BCErrorNotification': 'BigCommerce Error Notification',
  'BCHeartBeatNotification': 'BigCommerce Heartbeat Notification',
  'UnfinalizedYieldSheet': 'Unfinalized Yield Sheet Notification',
  'CustomerProfileReportEmail': 'Customer Profile Report Notification',
  'ClamsAndOysterReport': 'Clams and Oyster Report Notification',
  'ScrappingErrorEmail': 'Scraping Error Notification',
  'LargeOrderWithTwoDayGround': 'Large Order – 2-Day Ground Notification',
  'BCProductNotExist': 'BigCommerce Product Not Found Notification',
  'LargeOrderWithNextDayGround': 'Large Order – Next Day Ground Notification',
  'OrdersWithMoreThanOneFreeProducts': 'Orders With Multiple Free Products Notification',
  'EmailConfirm': 'Email Confirmation Notification',
  'EmailUpdateConfirm': 'Email Update Confirmation Notification',
  'IncorrectlyDelivered': 'Incorrectly Delivered Order Notification',
  'IGR': 'IGR Notification Notification',
  'MissedDeliveryDateErrorNotification': 'Missed Delivery Date Error Notification',
  'BCUnmappedProductReport': 'BigCommerce Unmapped Product Report Notification',
  'CutoffTimeNotification': 'Cutoff Time Notification Notification',
  'BCGiftCertificateReissuedOnOrderCancel': 'Gift Certificate Reissued on Order Cancel Notification',
  'VIPWithoutTransactions': 'VIP Without Transactions Notification',
  'BCWebhookProcessingErrorNotification': 'BigCommerce Webhook Processing Error Notification',
  'FedExUnknownEventDescription': 'FedEx Unknown Event Description Notification',
  'AdminSlowReport': 'Admin Portal Slowness Report',
};
const nameOf = (type) => LABELS[type] || type;

export default function EmailConfig() {
  const live = useWriteLive('ws8_email_config');
  const { rows, error, reload } = useRows(() => fetchEmailConfig(), []);
  const { save, saving, error: saveError } = useSave(reload);
  const [pickedId, setPickedId] = useState('');
  const [edit, setEdit] = useState(null);
  const [saved, setSaved] = useState(false);

  const list = useMemo(() => [...(rows || [])].sort((a, b) => nameOf(a.type).localeCompare(nameOf(b.type))), [rows]);
  const pick = (id) => {
    const r = list.find((x) => String(x.id) === id);
    setPickedId(id);
    setSaved(false);
    setEdit(r ? Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v == null ? '' : v])) : null);
  };
  const box = (key, label) => <Field label={label}><input className="form-input" disabled={!live} value={edit[key]} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} /></Field>;
  const submit = async () => { setSaved(!!(await save('email_config_save', edit))); };

  return (
    <div>
      <PageTitle>Email Config</PageTitle>
      <WriteOffNote live={live} what="Changes" />
      {error && <div className="staff-msg staff-msg-error">Error: {error}</div>}
      <div className="port-filters">
        <select className="form-select" aria-label="Email configuration" value={pickedId} onChange={(e) => pick(e.target.value)}>
          <option value="">{rows ? '-- Select an email configuration --' : 'Loading...'}</option>
          {list.map((r) => <option key={r.id} value={r.id}>{nameOf(r.type)}</option>)}
        </select>
      </div>
      {edit && (
        <div className="card">
          <div className="card-header">{nameOf(edit.type)}</div>
          <div className="modal-body">
            {box('from', 'From')}
            <Field label="To"><input className="form-input" disabled={!live || !!edit.toOverrideNote} value={edit.toOverrideNote || edit.to} onChange={(e) => setEdit({ ...edit, to: e.target.value })} /></Field>
            {box('bcc1', 'BCC1')}
            {box('bcc2', 'BCC2')}
            {box('bcc3', 'BCC3')}
            <Field label="Description"><textarea className="form-textarea" disabled={!live} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            {saveError && <div className="staff-msg staff-msg-error">{saveError}</div>}
            {saved && !saveError && <div className="staff-msg">Saved.</div>}
            <button type="button" className="btn btn-primary" disabled={!live || saving} onClick={submit}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
